import { useState, useEffect, useRef } from 'react'
import { shared } from '../assets/images'

// `enabled` permite pedir la ubicación solo cuando se necesita (por ejemplo, moto sin GPS).
const useUserLocation = ({ enabled = true } = {}) => {
  const [position, setPosition] = useState({})
  const [userLocation, setUserLocation] = useState(null)
  const [error, setError] = useState(null)
  const [isWatching, setIsWatching] = useState(false)

  const prevLocation = useRef(null)
  const watchIdRef = useRef(null)
  const retryTimeoutRef = useRef(null)
  const lastUpdateTime = useRef(0)
  const isMounted = useRef(true)

  const calculateDistance = (loc1, loc2) => {
    if (!loc1 || !loc2) return Infinity

    const R = 6371e3
    const φ1 = (loc1.latitude * Math.PI) / 180
    const φ2 = (loc2.latitude * Math.PI) / 180
    const Δφ = ((loc2.latitude - loc1.latitude) * Math.PI) / 180
    const Δλ = ((loc2.longitude - loc1.longitude) * Math.PI) / 180

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
  }

  const calculateSpeed = (loc1, loc2, timeDiff) => {
    if (!loc1 || !loc2 || !timeDiff) return 0
    const distance = calculateDistance(loc1, loc2)
    const speedMps = distance / (timeDiff / 1000)
    return Math.round(speedMps * 3.6)
  }

  const handlePosition = (position) => {
    if (!isMounted.current) return

    const currentTime = Date.now()
    const timeDiff = currentTime - lastUpdateTime.current

    if (prevLocation.current && timeDiff < 500) return
    // Llegó una posición: la ubicación funciona, aunque el usuario no se haya movido.
    setError(null)

    const newLocation = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
    }

    const newPositionFormat = {
      img: shared.motorNO,
      velocity: prevLocation.current
        ? calculateSpeed(prevLocation.current, newLocation, timeDiff).toString()
        : '0',
      turnOn: '1',
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      meters: position.coords.accuracy.toString(),
      onLine: '1',
      chasis: 'user',
      angle: position.coords.heading?.toString() || '0',
      time: position.timestamp,
      accuracy: position.coords.accuracy,
    }

    const distance = calculateDistance(prevLocation.current, newLocation)
    if (!prevLocation.current || distance > 1) {
      if (isMounted.current) {
        setUserLocation(newLocation)
        setPosition((prev) => ({
          ...prev,
          user: newPositionFormat,
        }))
      }

      prevLocation.current = newLocation
      lastUpdateTime.current = currentTime
    }
  }

  const handleError = (err) => {
    if (!isMounted.current) return

    let mensaje = 'Error al obtener la ubicación'
    switch (err.code) {
      case err.PERMISSION_DENIED:
        mensaje = 'Usuario denegó el acceso a la ubicación'
        setIsWatching(false)
        break
      case err.POSITION_UNAVAILABLE:
        mensaje = 'Información de ubicación no disponible'
        scheduleRetry(3000)
        break
      case err.TIMEOUT:
        mensaje = 'Se agotó el tiempo para obtener la ubicación'
        break
      default:
        scheduleRetry(3000)
    }
    console.error('Error de ubicación:', mensaje)
    // En computadores la alta precisión suele agotar el tiempo después de la primera
    // posición. Con una ubicación ya obtenida, solo se avisa si se quitó el permiso.
    const hasLocation = Boolean(prevLocation.current)
    if (isMounted.current && (err.code === err.PERMISSION_DENIED || !hasLocation)) {
      setError(mensaje)
    }
  }

  const scheduleRetry = (delay) => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
    retryTimeoutRef.current = setTimeout(startWatching, delay)
  }

  const startWatching = () => {
    if (!isMounted.current) return

    if (!navigator.geolocation) {
      setError('Tu navegador no soporta geolocalización')
      return
    }

    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current)
    }

    try {
      navigator.geolocation.getCurrentPosition(handlePosition, () => {}, {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 60000,
      })

      watchIdRef.current = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        }
      )

      if (isMounted.current) {
        setIsWatching(true)
      }
    } catch (e) {
      console.error('Error al iniciar el seguimiento:', e)
      if (isMounted.current) {
        setError('Error al iniciar el seguimiento de ubicación')
      }
    }
  }

  useEffect(() => {
    if (!enabled) return undefined
    isMounted.current = true
    lastUpdateTime.current = Date.now()
    startWatching()

    let permissionStatus = null
    const handlePermissionChange = () => {
      if (permissionStatus?.state === 'granted') startWatching()
    }
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((status) => {
        permissionStatus = status
        status.addEventListener('change', handlePermissionChange)
      })
      .catch(() => {})

    return () => {
      isMounted.current = false
      permissionStatus?.removeEventListener('change', handlePermissionChange)
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [enabled])

  return {
    position,
    userLocation,
    error,
    isWatching,
    startWatching,
  }
}

export default useUserLocation
